var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { resolveStrokeWidth, strokeContextForCanvas, getActiveDiagram } from '../theme';
import { createDiagramGroup, measureTextWidth, readCanvasSize, setDiagramState, } from '../helpers';
import { createCanEcuNode, } from '../primitives';
import { maybeApplyDiagramFlow } from '../flow';
/** Create CAN network diagram */
export function createCanNetwork(app, data, options) {
    var e_1, _a;
    var _b;
    if (options === void 0) { options = {}; }
    var group = createDiagramGroup(app, 'canNetwork', __assign(__assign({}, options), { data: data }), { name: 'canNetwork' });
    var canvas = readCanvasSize(options);
    var strokeCtx = strokeContextForCanvas(canvas.width, canvas.height);
    var nodeStroke = resolveStrokeWidth(getActiveDiagram().stroke.node, strokeCtx);
    var busY = 72;
    var ecuW = 96;
    var margin = 36;
    var n = Math.max(1, data.ecus.length);
    // Stretch bus across the canvas so every ECU is fully visible with even gaps
    var busWidth = Math.max(280, canvas.width - margin * 2);
    var busLabel = (_b = data.busLabel) !== null && _b !== void 0 ? _b : 'CAN Bus';
    // Persist for flow hop geometry (packet rides this rail)
    setDiagramState(group, { canBusY: busY });
    var busGlow = getActiveDiagram().canBusGlow;
    var busFill = getActiveDiagram().canBus;
    group.add(app.roundedRect({
        x: margin - 4,
        y: busY - 11,
        width: busWidth + 8,
        height: 22,
        cornerRadius: 6,
        fill: busGlow,
        stroke: null,
        opacity: 0.35,
        listening: false,
    }));
    // CAN-H (upper) / CAN-L (lower) — distinct opacity for dual-line bus
    group.add(app.line({
        x: margin,
        y: busY - 4,
        x2: busWidth,
        y2: 0,
        stroke: busFill,
        strokeWidth: 2.5,
        lineCap: 'round',
        listening: false,
    }));
    group.add(app.line({
        x: margin,
        y: busY + 4,
        x2: busWidth,
        y2: 0,
        stroke: busFill,
        strokeWidth: 2.5,
        lineCap: 'round',
        opacity: 0.55,
        listening: false,
    }));
    // Termination: twin vertical bars (schematic-style resistors), not status dots
    var termColor = getActiveDiagram().canTermination;
    try {
        for (var _c = __values([margin, margin + busWidth]), _d = _c.next(); !_d.done; _d = _c.next()) {
            var tx = _d.value;
            group.add(app.line({
                x: tx,
                y: busY - 10,
                x2: 0,
                y2: 20,
                stroke: termColor,
                strokeWidth: 2.5,
                lineCap: 'round',
                listening: false,
            }));
            group.add(app.line({
                x: tx + (tx === margin ? 5 : -5),
                y: busY - 10,
                x2: 0,
                y2: 20,
                stroke: termColor,
                strokeWidth: 2.5,
                lineCap: 'round',
                listening: false,
            }));
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
        }
        finally { if (e_1) throw e_1.error; }
    }
    var labelW = measureTextWidth(busLabel, getActiveDiagram().fontSize.base, 'bold');
    group.add(app.text({
        text: busLabel,
        x: margin + busWidth / 2 - labelW / 2,
        y: busY - 30,
        fontSize: getActiveDiagram().fontSize.base,
        fill: getActiveDiagram().edge,
        fontWeight: 'bold',
        fontFamily: getActiveDiagram().fontFamily,
        listening: false,
    }));
    var spacing = busWidth / (n + 1);
    for (var i = 0; i < data.ecus.length; i++) {
        var ecu = data.ecus[i];
        var ecuColor = getActiveDiagram().canEcuPalette[i % getActiveDiagram().canEcuPalette.length];
        var x = margin + spacing * (i + 1) - ecuW / 2;
        var ecuGroup = createCanEcuNode(app, ecu.label, ecu.address, ecuColor, nodeStroke);
        ecuGroup.x = x;
        // Top of card sits 18px below bus midline → tap of 18 meets CAN-H/L center
        ecuGroup.y = busY + 18;
        ecuGroup.metadata = __assign(__assign({}, ecuGroup.metadata), { diagramId: ecu.id });
        group.add(ecuGroup);
    }
    maybeApplyDiagramFlow(app, group, options);
    return group;
}
